import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { PREDICTION } from '@/tools/prediction/prediction.constants';
import { adjustPrincipalComponentsToKeep } from '@/utilities/predictionHelper.utilities';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';

type PredictionScale = (typeof PREDICTION.SCALE)[keyof typeof PREDICTION.SCALE];
type RegressionMethod = (typeof PREDICTION.REGRESSION_METHODS)[keyof typeof PREDICTION.REGRESSION_METHODS];

interface WindowRange {
  startTime: number;
  endTime: number;
}

interface TrainingWindowPayload {
  window: WindowRange;
}

interface RefreshRatePayload {
  refreshRate: FrontendDuration;
}

interface PrincipalComponentsPayload {
  principalComponentsToKeep: number | null;
}

export interface PredictionPanelConfig {
  lambda: number;
  mustGoThroughZero: boolean;
  option: PredictionScale;
  polynomialValue?: number;
  windowStart: number;
  windowEnd: number;
  regressionMethod: RegressionMethod;
  variableSelection: number;
  variableSelectionEnabled: boolean;
  refreshRate: FrontendDuration;
  isRefreshRateEnabled: boolean;
  type: string;
}

interface PredictionPanelState extends BaseToolPersistedState {
  targetSignal?: ToolItem | null;
  condition?: ToolItem | null;
  inputSignals?: ToolItem[] | null;
  option: PredictionScale;
  polynomialValue: number;
  mustGoThroughZero: boolean;
  lambda: number;
  windowStart: number;
  windowEnd: number;
  window: any; // any because of Baobab's `monkey()`
  modelExpanded: boolean;
  regressionMethod: RegressionMethod;
  variableSelection: number;
  refreshRate: FrontendDuration;
  isRefreshRateEnabled: boolean;
  variableSelectionEnabled: boolean;
  principalComponentsToKeepByUser?: number | null;
  color: string;
}

export type PredictionPanelDehydratedState = Omit<PredictionPanelState, 'window'>;

export class PredictionPanelStore extends BaseToolStore {
  static readonly storeName = 'sqPredictionPanelStore';
  type = TREND_TOOLS.PREDICTION;
  parameterDefinitions = {
    targetSignal: { predicate: ['name', 'target'] },
    condition: { predicate: ['name', 'condition'] },
    inputSignals: {
      predicate: (parameter: ToolParameter) => !_.includes(['target', 'condition'], parameter.name ?? ''),
      multiple: true,
    },
  };

  initialize(): void {
    const initialState: PredictionPanelState = {
      ...BASE_TOOL_COMMON_PROPS,
      option: PREDICTION.SCALE.LINEAR,
      polynomialValue: 2,
      mustGoThroughZero: false,
      lambda: 0,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelExpanded: false,
      regressionMethod: PREDICTION.REGRESSION_METHODS.ORDINARY_LEAST_SQUARES,
      variableSelection: 0.05,
      refreshRate: { value: 30, units: 'min' },
      isRefreshRateEnabled: true,
      variableSelectionEnabled: false,
      color: '',
    };
    this.state = this.immutable(initialState);
  }

  get color(): string {
    return this.state.get('color');
  }

  get targetSignal(): ToolItem | undefined {
    return this.state.get('targetSignal');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get inputSignals(): ToolItem[] | undefined {
    return this.state.get('inputSignals');
  }

  get option(): PredictionScale {
    return this.state.get('option');
  }

  get polynomialValue(): number {
    return this.state.get('polynomialValue');
  }

  get mustGoThroughZero(): boolean {
    return this.state.get('mustGoThroughZero');
  }

  get lambda(): number {
    return this.state.get('lambda');
  }

  get principalComponentsToKeep(): number | null {
    return adjustPrincipalComponentsToKeep(
      this.state.get('inputSignals'),
      this.state.get('option'),
      this.state.get('polynomialValue'),
      this.state.get('principalComponentsToKeepByUser'),
    );
  }

  get window(): WindowRange {
    return this.state.get('window');
  }

  get modelExpanded(): boolean {
    return this.state.get('modelExpanded');
  }

  get regressionMethod(): RegressionMethod {
    return this.state.get('regressionMethod');
  }

  get variableSelection(): number {
    return this.state.get('variableSelection');
  }

  get refreshRate(): FrontendDuration {
    return this.state.get('refreshRate');
  }

  get isRefreshRateEnabled(): boolean {
    return this.state.get('isRefreshRateEnabled');
  }

  get variableSelectionEnabled(): boolean {
    return this.state.get('variableSelectionEnabled');
  }

  get predictionConfig(): PredictionPanelConfig {
    return {
      lambda: this.lambda,
      mustGoThroughZero: this.mustGoThroughZero,
      option: this.option,
      polynomialValue: this.polynomialValue,
      windowStart: this.state.get('windowStart'),
      windowEnd: this.state.get('windowEnd'),
      regressionMethod: this.regressionMethod,
      variableSelection: this.variableSelection,
      variableSelectionEnabled: this.variableSelectionEnabled,
      refreshRate: this.refreshRate,
      isRefreshRateEnabled: this.isRefreshRateEnabled,
      type: this.type,
    };
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): PredictionPanelDehydratedState {
    return this.state.serialize();
  }

  /**
   * Sets the prediction panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<PredictionPanelDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  /**
   * Set the prediction option that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {String} payload.option- The prediction option; one of PREDICTION.SCALE
   */
  setOption(payload: { option: PredictionScale }): void {
    this.state.set('option', payload.option);
  }

  /**
   * Set the polynomial value that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.polynomialValue - The polynomial value
   */
  setPolynomialValue(payload: { polynomialValue: number }): void {
    this.state.set('polynomialValue', payload.polynomialValue);
  }

  /**
   * Set the 'must go through zero' value that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Boolean} payload.mustGoThroughZero - the 'must go through zero' options value
   */
  setMustGoThroughZero(payload: { mustGoThroughZero: boolean }): void {
    this.state.set('mustGoThroughZero', payload.mustGoThroughZero);
  }

  /**
   * Set the lambda value that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.lambda - the lambda options value
   */
  setLambda(payload: { lambda: number }): void {
    this.state.set('lambda', payload.lambda);
  }

  /**
   * Set the 'principal components to keep' value that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.principalComponentsToKeep - the 'principal components to keep' options value
   */
  setPrincipalComponentsToKeepByUser(payload: PrincipalComponentsPayload): void {
    // We need the state change to always be called in case the saved principalComponentsToKeepByUser value is
    // no longer a valid value for the current inputs.
    this.state.set('principalComponentsToKeepByUser', null);
    this.state.set('principalComponentsToKeepByUser', payload.principalComponentsToKeep);
  }

  /**
   * Set the reference window start value that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.window.startTime - the start of the reference training window
   * @param {Number} payload.window.endTime - the end of the reference training window
   */
  setTrainingWindow(payload: TrainingWindowPayload): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  /**
   * Set the model expanded state that is used in the form.
   *
   *
   * @param {Object} payload - Object container
   * @param {Boolean} payload.modelExpanded - the model expanded state
   */
  setModelExpanded(payload: { modelExpanded: boolean }): void {
    this.state.set('modelExpanded', payload.modelExpanded);
  }

  /**
   * Set the regression method that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {String} payload.regressionMethod - the regression method (one of REGRESSION_METHODS).
   */
  setRegressionMethod(payload: { regressionMethod: RegressionMethod }): void {
    this.state.set('regressionMethod', payload.regressionMethod);
  }

  setRefreshRate(payload: RefreshRatePayload): void {
    this.state.set('refreshRate', payload.refreshRate);
  }

  /**
   * Set whether refresh rate is enabled or not.
   */
  setIsRefreshRateEnabled(payload: { isRefreshRateEnabled: boolean }): void {
    this.state.set('isRefreshRateEnabled', payload.isRefreshRateEnabled);
  }

  /**
   * Set the variable selection that is used in the form.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.variableSelection - the variable selection (0-1)
   */
  setVariableSelection(payload: { variableSelection: number }): void {
    this.state.set('variableSelection', payload.variableSelection);
  }

  /**
   * Set whether variable selection is enabled or not.
   *
   * @param {Object} payload - Object container
   * @param {Boolean} payload.variableSelectionEnabled - true of variable selection is enabled, false otherwise
   */
  setVariableSelectionEnabled(payload: { variableSelectionEnabled: boolean }): void {
    this.state.set('variableSelectionEnabled', payload.variableSelectionEnabled);
  }

  localHandlers = {
    PREDICTION_SET_OPTION: this.setOption,
    PREDICTION_SET_POLYNOMIAL_VALUE: this.setPolynomialValue,
    PREDICTION_SET_MUST_GO_THROUGH_ZERO: this.setMustGoThroughZero,
    PREDICTION_SET_LAMBDA: this.setLambda,
    PREDICTION_SET_PRINCIPAL_COMPONENTS_TO_KEEP: this.setPrincipalComponentsToKeepByUser,
    PREDICTION_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PREDICTION_SET_MODEL_EXPANDED: this.setModelExpanded,
    PREDICTION_SET_REGRESSION_METHOD: this.setRegressionMethod,
    PREDICTION_SET_VARIABLE_SELECTION: this.setVariableSelection,
    PREDICTION_SET_REFRESH_RATE: this.setRefreshRate,
    PREDICTION_SET_IS_REFRESH_RATE_ENABLED: this.setIsRefreshRateEnabled,
    PREDICTION_SET_VARIABLE_SELECTION_ENABLED: this.setVariableSelectionEnabled,
    PREDICTION_SET_COLOR: ({ color }: { color: string }): void => {
      this.state.set('color', color);
    },
  };

  handlers = _.assign({}, super.baseHandlers, this.localHandlers);
}
